import { useState, useMemo } from 'react';
import { C } from '../../../lib/tokens.js';
import { useUser } from '../../../lib/UserCacheContext.jsx';
import { useLookups } from '../../../lib/LookupsContext.jsx';
import { relTime, dateTimeFull, dateLong, dateShort } from '../../../lib/utils.js';
import { StatusChip, Chip, PriorityChip, TypeChip } from '../../../components/ui/Chips.jsx';
import { Avatar } from '../../../components/ui/Avatar.jsx';
import { IconText } from '../../../components/ui/Misc.jsx';
import { useValidRequests, InfiniteScrollList } from './shared.jsx';

export function RequestTimeline({ requests, onOpen, showPriority, hasMore, onLoadMore, loadingMore }) {
  const validRequests = useValidRequests(requests);

  const groups = useMemo(() => {
    const map = new Map();
    [...validRequests].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)).forEach(r => {
      const d = dateShort(r.updatedAt);
      if (!map.has(d)) map.set(d, []);
      map.get(d).push(r);
    });
    return [...map.entries()];
  }, [validRequests]);

  return (
    <InfiniteScrollList hasMore={hasMore} onLoadMore={onLoadMore} loadingMore={loadingMore}
      style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>
      <div style={{ maxWidth: 880, margin: '0 auto' }}>
        {groups.map(([day, items]) => (
          <div key={day} style={{ marginBottom: 28 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, fontSize: 11, fontWeight: 500, color: C.iron, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              <span>{dateLong(day)}</span>
              <span style={{ flex: 1, height: 1, background: C.mercury }} />
              <span style={{ color: C.silver }}>{items.length} updates</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {items.map(req => <TimelineRow key={req.id} req={req} onOpen={onOpen} showPriority={showPriority} />)}
            </div>
          </div>
        ))}
      </div>
    </InfiniteScrollList>
  );
}

function TimelineRow({ req, onOpen, showPriority }) {
  const [h, setH] = useState(false);
  const assignee = useUser(req?.assigneeId);
  if (!req) return null;
  return (
    <div onClick={() => onOpen(req)} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 14, padding: 14, borderRadius: 6,
        background: h ? C.tin : 'transparent', border: `1px solid ${h ? C.silver : 'transparent'}`,
        cursor: 'pointer', transition: 'all 120ms', boxShadow: h ? '0 2px 6px rgba(34,52,105,0.08)' : 'none',
      }}>
      <div style={{ width: 32, height: 32, borderRadius: '50%', background: C.tin, border: `1px solid ${C.mercury}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
        <StatusDot id={req.status} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: 'Roboto Mono', fontSize: 11, color: C.liquid, fontWeight: 500 }}>#{req.docNumber ?? req.id}</span>
          <TypeChip id={req.type} size="sm" readOnly /><StatusChip id={req.status} size="sm" filled readOnly />
          {showPriority ? <PriorityChip id={req.priority} size="sm" readOnly /> : null}
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 11, color: C.iron }} title={dateTimeFull(req.updatedAt)}>{relTime(req.updatedAt)}</span>
        </div>
        <div style={{ fontSize: 13, color: C.fg, fontWeight: 500, marginBottom: 4 }}>{req.summary}</div>
        <div style={{ display: 'flex', gap: 12, fontSize: 11, color: C.iron, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Avatar user={assignee} size={16} /> {assignee?.name}</span>
          {req.comments > 0    ? <IconText icon="forum"       size={11}>{req.comments} comments</IconText> : null}
          {req.attachmentCount > 0 ? <IconText icon="attach_file" size={11}>{req.attachmentCount}</IconText> : null}
          {req.fixedIn ? <Chip color="#3F7A00" bg="#EEF8DD" icon="rocket_launch" size="sm">Fixed in {req.fixedIn}</Chip> : null}
        </div>
      </div>
    </div>
  );
}

function StatusDot({ id }) {
  const { statuses } = useLookups();
  const s = statuses.find(x => x.id === id);
  return <span className="material-icons" style={{ fontSize: 18, color: s?.color || C.silver }}>{s?.icon || 'circle'}</span>;
}